import {
  SchoolFragment,
  useLicensorsLazyQuery,
  usePathCreatorResourcesLazyQuery
} from "@pathwright/graphql-client/src/generated"
import { useGetNewResource } from "../../path/create/CreateOrSelectResource"
import { massageLicensorsQueryData } from "../../path/create/Licensors"
import { usePathCreatorContext } from "../../path/create/PathCreatorContext"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

// Sets the initial value of the AddPathFlow based on current resources and licensors.
const useAddPathInitialValue = () => {
  const { school } = usePathwrightContext()
  const { initialValue, setInitialValue } = usePathCreatorContext()

  const { getNewResource, loading: loadingNewResource } = useGetNewResource()

  const [licensorsQuery, { loading: loadingLicensors }] =
    useLicensorsLazyQuery()

  const [resourcesQuery, { loading: loadingResources }] =
    usePathCreatorResourcesLazyQuery()

  async function handleSetInitialValue() {
    let licensors: SchoolFragment[] = []

    // Both schools with curriculum subscriptions and schools with store links will have
    // `is_curriculum_subscriber` set to true. We can therefore skip the query for non-subscriber schools.
    // Keep these as separate Promise.all arms so PathCreatorResourcesQuery and LicensorsQuery
    // (both expose storeLinks with different field selections) are not widened into one array type.
    // network-only: library can change while Home is open (e.g. after licensing from the store).
    const resourcesPromise = resourcesQuery({ fetchPolicy: "network-only" })
    const licensorsPromise = school.is_curriculum_subscriber
      ? licensorsQuery({ fetchPolicy: "network-only" })
      : Promise.resolve(undefined)

    const [resourcesQueryResult, licensorsQueryResult] = await Promise.all([
      resourcesPromise,
      licensorsPromise
    ])

    if (licensorsQueryResult) {
      licensors = massageLicensorsQueryData(licensorsQueryResult.data)
    }

    const hasLicensors = licensors.length > 0

    const hasResources =
      (resourcesQueryResult.data?.resources?.edges?.length || 0) > 0
    // Only set the initial value if it is not already set.
    if (initialValue === undefined) {
      // If there are no licensors and no resources, we set the initial value in such a way so as to select the "create" option.
      if (!hasLicensors && !hasResources) {
        setInitialValue(await getNewResource())
      } else {
        // Selecting null (as opposed to undefined) to allow user to choose between creating new or selecting existing resource.
        setInitialValue(null)
      }
    }
  }

  return {
    initialValue,
    setInitialValue: handleSetInitialValue,
    loading: loadingNewResource || loadingLicensors || loadingResources
  }
}

export default useAddPathInitialValue
